import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedDestinations = () => {
  const navigate = useNavigate();

  const featuredDestinations = [
    {
      id: 1,
      name: "Bali, Indonesia",
      image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=800&h=600&fit=crop",
      tours: 45,
      startingPrice: 299,
      rating: 4.8,
      reviews: 1250,
      description: "Tropical paradise with stunning beaches, ancient temples, and vibrant culture",
      badge: "Most Popular",
      badgeColor: "bg-accent"
    },
    {
      id: 2,
      name: "Santorini, Greece",
      image: "https://images.pexels.com/photos/161901/santorini-greece-island-161901.jpeg?w=800&h=600&fit=crop",
      tours: 32,
      startingPrice: 450,
      rating: 4.9,
      reviews: 890,
      description: "Iconic white-washed buildings overlooking the stunning Aegean Sea",
      badge: "Trending",
      badgeColor: "bg-primary"
    },
    {
      id: 3,
      name: "Kyoto, Japan",
      image: "https://images.pixabay.com/photo/2020/05/18/16/17/cherry-blossoms-5187041_1280.jpg?w=800&h=600&fit=crop",
      tours: 28,
      startingPrice: 380,
      rating: 4.7,
      reviews: 675,
      description: "Ancient temples, traditional gardens, and authentic Japanese culture",
      badge: "Cultural",
      badgeColor: "bg-secondary"
    },
    {
      id: 4,
      name: "Machu Picchu, Peru",
      image: "https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=800&h=600&fit=crop",
      tours: 18,
      startingPrice: 520,
      rating: 4.9,
      reviews: 445,
      description: "Ancient Incan citadel high in the Andes Mountains",
      badge: "Adventure",
      badgeColor: "bg-success"
    },
    {
      id: 5,
      name: "Paris, France",
      image: "https://images.pexels.com/photos/338515/pexels-photo-338515.jpeg?w=800&h=600&fit=crop",
      tours: 67,
      startingPrice: 275,
      rating: 4.6,
      reviews: 2100,
      description: "City of lights with iconic landmarks, world-class museums, and cuisine",
      badge: "Classic",
      badgeColor: "bg-warning"
    },
    {
      id: 6,
      name: "Dubai, UAE",
      image: "https://images.pixabay.com/photo/2020/03/04/14/41/dubai-4900473_1280.jpg?w=800&h=600&fit=crop",
      tours: 41,
      startingPrice: 350,
      rating: 4.8,
      reviews: 1580,
      description: "Modern metropolis with luxury shopping, futuristic architecture, and desert adventures",
      badge: "Luxury",
      badgeColor: "bg-accent"
    }
  ];

  const handleDestinationClick = (destination) => {
    navigate(`/search-results?destination=${encodeURIComponent(destination?.name)}`);
  };

  const handleViewAllClick = () => {
    navigate('/search-results');
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Featured Destinations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Discover the world's most captivating destinations with our carefully curated selection of tours and experiences
          </p>
          <Button
            variant="outline"
            onClick={handleViewAllClick}
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All Destinations
          </Button>
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredDestinations?.map((destination, index) => (
            <div
              key={destination?.id}
              className="group bg-card rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover-lift animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleDestinationClick(destination)}
            >
              {/* Image Container */}
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={destination?.image}
                  alt={destination?.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Badge */}
                <div className={`absolute top-4 left-4 ${destination?.badgeColor} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                  {destination?.badge}
                </div>

                {/* Rating */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-warning fill-current" />
                  <span className="text-sm font-medium text-foreground">{destination?.rating}</span>
                </div>

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    {destination?.name}
                  </h3>
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="MapPin" size={16} />
                    <span className="text-sm">{destination?.tours} tours</span>
                  </div>
                </div>

                <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                  {destination?.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-warning fill-current" />
                      <span className="text-sm font-medium text-foreground">{destination?.rating}</span>
                      <span className="text-sm text-muted-foreground">({destination?.reviews})</span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">From</div>
                    <div className="text-lg font-bold text-primary">${destination?.startingPrice}</div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-4 pt-4 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="w-full group-hover:bg-primary group-hover:text-white transition-colors"
                  >
                    Explore Tours
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Can't find your dream destination?
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/search-results')}
            iconName="Search"
            iconPosition="left"
          >
            Browse All Destinations
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedDestinations;